import React from 'react';
import {Link} from 'react-router';

import {useOnboarding} from '../Onboarding/useOnboarding';
import {ResourceNotice, ResourceQueryState} from './LoadingCard';
import {RESOURCE_TITLE_CLASSES, ResourceBreadcrumbs} from './ProCategories';

/**
 * The transition stages as an ordered list. Each card is one stretched link
 * named by the stage, so the whole card stays the pointer target the MUI
 * CardActionArea was, and list position carries the number the decorative
 * marker shows.
 *
 * The stages come from OnboardingProvider, which reads them once. A read that
 * failed is shown as the resource failure with Retry, which reads them again,
 * rather than as a load that never ends.
 */
const ProKeyDuties = () => {
  const {onboardingState, fetchStages} = useOnboarding();
  const stages = onboardingState?.stages;

  return (
    <div data-testid="pro-key-duties">
      <ResourceBreadcrumbs items={[{label: 'Home', to: '/dashboard'}, {label: 'Key Duties'}]} />
      <h1 className={`mb-4 text-center ${RESOURCE_TITLE_CLASSES}`}>Key Duties</h1>
      <p className="m-0 mb-2 text-center text-(--compass-on-surface)">
        Select a stage to view the duties for your profession within that stage.
      </p>
      <p className="m-0 mb-6 text-center text-sm text-(--compass-on-surface)">
        The stage descriptions are the same as those used in Compass for Parents.
      </p>
      <ResourceQueryState
        data={stages}
        error={onboardingState?.stagesFailed}
        refetch={fetchStages}
        label="transition stages">
        {stages?.length ? (
          <ol
            data-testid="pro-key-duty-stages"
            className="m-0 mx-auto flex max-w-3xl list-none flex-col gap-4 p-0">
            {stages.map((stage, index) => (
              <li key={stage.id} className="flex min-w-0 items-start gap-3 sm:gap-4">
                <span
                  aria-hidden="true"
                  className="mt-4 flex size-10 shrink-0 items-center justify-center rounded-full bg-(--compass-primary) font-semibold text-(--compass-on-primary)">
                  {index + 1}
                </span>
                <div className="relative min-w-0 flex-1 rounded-md border border-(--compass-border) bg-(--compass-surface) p-4 text-(--compass-on-surface) shadow-sm hover:bg-[image:linear-gradient(var(--compass-row-hover),var(--compass-row-hover))] has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-(--compass-focus-ring)">
                  <h2 className="m-0 text-lg font-semibold break-words">
                    <Link
                      to={`stage/${encodeURIComponent(stage.slug)}`}
                      className="inline-flex min-h-11 items-center text-(--compass-on-surface) no-underline outline-none after:absolute after:inset-0 after:rounded-md">
                      {stage.name}
                    </Link>
                  </h2>
                  {stage.medium_label ? (
                    <p className="m-0 mt-1 text-sm font-medium">{stage.medium_label}</p>
                  ) : null}
                  {stage.description ? (
                    <p className="m-0 mt-2 text-sm break-words">{stage.description}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <ResourceNotice>No transition stages are available yet.</ResourceNotice>
        )}
      </ResourceQueryState>
    </div>
  );
};

export default ProKeyDuties;
